import { useState, useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { CreateViewMutation } from "core/mutations";
import getClientIp from "core/utils/getClientIp";
import { getUtmFromLocalStorage } from "core/utils/utm";

/** Кеш playlist по contentId — один createView на ролик в сессии ленты */
const playbackCache = new Map();

function cacheEntry(contentId) {
  return playbackCache.get(contentId);
}

function setCacheReady(contentId, data) {
  playbackCache.set(contentId, { status: "ready", data });
}

function setCacheLoading(contentId, promise) {
  playbackCache.set(contentId, { status: "loading", promise });
}

async function fetchPlaybackUrl(client, content) {
  const variables = {
    token: content.access.token,
    utm: getUtmFromLocalStorage(),
  };

  const ip = await getClientIp(content.getIpUrl);
  if (ip) {
    variables.clientIp = ip;
  }

  const refetchQueries = content.premium ? ["Viewer"] : [];

  const { data } = await client.mutate({
    mutation: CreateViewMutation,
    variables,
    refetchQueries,
  });

  const view = data?.createView;
  const video = view?.video;

  if (!view?.id || !video?.playlist) {
    throw new Error("Shorty playback: missing playlist");
  }

  return {
    playbackUrl: video.playlist,
    videoId: video.id,
    viewId: view.id,
    drm: video?.drmPlayerSettings,
    content,
  };
}

/**
 * Получает signed playlist URL для active и preload-соседей.
 * createView вызывается без дубля на тот же contentId (кеш).
 */
export default function useShortyPlaybackUrl({ content, enabled }) {
  const client = useApolloClient();
  const [playbackData, setPlaybackData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const contentIdRef = useRef(null);

  useEffect(() => {
    contentIdRef.current = content?.id ?? null;
  }, [content?.id]);

  useEffect(() => {
    if (!enabled || !content?.access?.token) {
      setPlaybackData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const contentId = content.id;
    let cancelled = false;

    const applyData = (data) => {
      if (cancelled || contentIdRef.current !== contentId) return;
      setPlaybackData(data);
      setLoading(false);
      setError(null);
    };

    const applyError = (err) => {
      if (cancelled || contentIdRef.current !== contentId) return;
      setError(err);
      setLoading(false);
      setPlaybackData(null);
    };

    const existing = cacheEntry(contentId);

    if (existing?.status === "ready") {
      applyData(existing.data);
      return;
    }

    if (existing?.status === "loading") {
      setLoading(true);
      existing.promise.then(applyData).catch(applyError);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    const promise = fetchPlaybackUrl(client, content)
      .then((data) => {
        setCacheReady(contentId, data);
        return data;
      })
      .catch((err) => {
        playbackCache.delete(contentId);
        throw err;
      });

    setCacheLoading(contentId, promise);
    promise.then(applyData).catch(applyError);

    return () => {
      cancelled = true;
    };
  }, [enabled, content, client]);

  return { playbackData, loading, error };
}
